import { faCheck, faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Menu } from '@headlessui/react'
import React, { useRef, useState } from 'react'
import { GetLaneId, LN_ALLOWED, MM_ALLOWED, PROVIDER_LANES, providerPermissionFields } from './providersData'
import { ParseErrors } from '../../utils/ParseErrors'
import { toast } from 'react-toastify'
import axiosInstance from '../../utils/axiosInstance'
import { IProviderOptions } from '../../types/provider-types'
import GetUser from '../../utils/getUser'
import DropdownPanel from '../DropdownPanel'
import clsx from 'clsx'

interface IProviderIconSelect {
    providerId: number
    field: (typeof providerPermissionFields)[number]
    value: IProviderOptions
    lnSubtag: string[]
    mmSubtag: string[]
    UpdateItemData: (data: Record<string, any>) => void
}

const SubTag = ({ text }: { text: string }) => (
    <span className='h-3.5 px-[3px] rounded-[4px] bg-gradient-to-r from-accent to-accent-bright text-[8px] font-bold leading-[14px] text-white ring-2 ring-surface'>
        {text}
    </span>
)

const ProviderIconSelect: React.FC<IProviderIconSelect> = ({ providerId, field, value, lnSubtag, mmSubtag, UpdateItemData }) => {
    const currentUser = GetUser()
    const [loading, setLoading] = useState<boolean>(false)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const lane = PROVIDER_LANES.find((laneItem) => laneItem.id === GetLaneId(value))
    const hasLn = lnSubtag?.includes(field.id)
    const hasMm = mmSubtag?.includes(field.id)
    const title = `${field.label}: ${lane.label}${hasLn ? ' · LN' : ''}${hasMm ? ' · MM' : ''}`


    // ---------------------------- [ UPDATE DOCUMENT RULE ] ---------------------------

    const UpdateRule = async (type: IProviderOptions) => {
        setLoading(true)
        try {
            await axiosInstance.patch(`/provider/${providerId}`, { [field.id]: type })
            UpdateItemData({ [field.id]: type })
            toast.success("Updated Successfully")
        } catch (error) {
            toast.error(ParseErrors(error))
        }
        setLoading(false)
    }


    // ---------------------------- [ TOGGLE LN / MM TAG ] ---------------------------

    const ToggleSubTag = async (subType: "ln" | "mm") => {
        setLoading(true)
        try {
            const res = await axiosInstance.patch(`/provider/tags/${providerId}`, subType === "ln" ? { lnSubtag: field.id } : { mmSubtag: field.id })
            UpdateItemData(subType === "ln" ? { lnSubtag: res.data.data.lnSubtag } : { mmSubtag: res.data.data.mmSubtag })
            toast.success("Updated Successfully")
        } catch (error) {
            toast.error(ParseErrors(error))
        }
        setLoading(false)
    }


    const cellBody = (
        <>
            <FontAwesomeIcon icon={loading ? faCircleNotch : lane.icon} className={clsx("text-[12px]", loading && "fa-spin")} />
            {(hasLn || hasMm) && <span className='absolute -bottom-1.5 left-1/2 -translate-x-1/2 flex gap-0.5'>
                {hasLn && <SubTag text="LN" />}
                {hasMm && <SubTag text="MM" />}
            </span>}
        </>
    )
    const cellClass = clsx("relative h-8 w-8 mx-auto rounded-lg flex items-center justify-center transition-all duration-200", lane.cell)

    if (currentUser?.type !== "super-admin") {
        return <div title={title} aria-label={title} className={cellClass}>{cellBody}</div>
    }

    const tagOptions = [
        LN_ALLOWED.includes(field.id) && { id: "ln" as const, label: "LN tag", active: hasLn },
        MM_ALLOWED.includes(field.id) && { id: "mm" as const, label: "MM tag", active: hasMm },
    ].filter(Boolean)

    return (
        <Menu as="div" className="relative">
            {({ open }) => (
                <>
                    <Menu.Button
                        ref={buttonRef}
                        disabled={loading}
                        title={title}
                        aria-label={title}
                        className={clsx(cellClass, "cursor-pointer hover:opacity-100 hover:ring-1 hover:ring-indigo-400/60 hover:scale-105", open && "!opacity-100 ring-2 !ring-indigo-400/70")}
                    >
                        {cellBody}
                    </Menu.Button>

                    <DropdownPanel open={open} anchorRef={buttonRef} align="left" width={224}>
                        <Menu.Items static className="py-1.5 rounded-xl bg-surface-elevated border border-edge shadow-overlay focus:outline-none">
                            <div className="px-3 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-light-gray">
                                {field.label}
                            </div>
                            {PROVIDER_LANES.map((laneItem) => (
                                <Menu.Item key={laneItem.id}>
                                    {({ active }) => (
                                        <button
                                            type="button"
                                            onClick={() => laneItem.id !== lane.id && UpdateRule(laneItem.id)}
                                            className={clsx("w-full flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium text-text-primary", active && "bg-overlay-hover")}
                                        >
                                            <span className={clsx("h-6 w-6 rounded-md flex items-center justify-center", laneItem.cell, "!opacity-100")}>
                                                <FontAwesomeIcon icon={laneItem.icon} className="text-[10px]" />
                                            </span>
                                            {laneItem.label}
                                            {laneItem.id === lane.id && <FontAwesomeIcon icon={faCheck} className="ml-auto text-[11px] text-accent" />}
                                        </button>
                                    )}
                                </Menu.Item>
                            ))}
                            {tagOptions.length > 0 && <div className="my-1.5 border-t border-edge-faint" />}
                            {tagOptions.map((tag) => (
                                <Menu.Item key={tag.id}>
                                    {({ active }) => (
                                        <button
                                            type="button"
                                            onClick={() => ToggleSubTag(tag.id)}
                                            className={clsx("w-full flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium text-text-primary", active && "bg-overlay-hover")}
                                        >
                                            <span className="h-6 w-6 flex items-center justify-center"><SubTag text={tag.id.toUpperCase()} /></span>
                                            {tag.label}
                                            {tag.active && <FontAwesomeIcon icon={faCheck} className="ml-auto text-[11px] text-accent" />}
                                        </button>
                                    )}
                                </Menu.Item>
                            ))}
                        </Menu.Items>
                    </DropdownPanel>
                </>
            )}
        </Menu>
    )
}

export default ProviderIconSelect
